import React, { useState, useEffect } from "react";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaCoins,
  FaUniversity,
  FaBitcoin,
  FaCog,
  FaBox,
  FaPlus,
} from "react-icons/fa";
import AssetModal from "./components/AssetModal";

const AssetList = () => {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("current");
  const [selectedSubType, setSelectedSubType] = useState("cash");
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [modalType, setModalType] = useState(""); // 'view', 'edit', 'delete'

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [assets, selectedCategory, selectedSubType]);

  const fetchAssets = async () => {
    setIsLoading(true);
    try {
      // Fetch all asset types
      const [cashRes, bankRes, cryptoRes, fixedRes, tradingRes] =
        await Promise.all([
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash`,
            {
              credentials: "include",
            }
          ),
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank`,
            {
              credentials: "include",
            }
          ),
          fetch(
            `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/crypto`,
            {
              credentials: "include",
            }
          ),
          fetch(`${process.env.REACT_APP_BASE_URL}/api/assets/fixed-assets`, {
            credentials: "include",
          }),
          fetch(`${process.env.REACT_APP_BASE_URL}/api/assets/trading-assets`, {
            credentials: "include",
          }),
        ]);

      const [cashData, bankData, cryptoData, fixedData, tradingData] =
        await Promise.all([
          cashRes.json(),
          bankRes.json(),
          cryptoRes.json(),
          fixedRes.json(),
          tradingRes.json(),
        ]);

      const allAssets = [
        ...(cashData.cash_accounts || []).map((asset) => ({
          ...asset,
          type: "cash",
          category: "Current Assets",
        })),
        ...(bankData.bank_accounts || []).map((asset) => ({
          ...asset,
          type: "bank",
          category: "Current Assets",
        })),
        ...(cryptoData.crypto_accounts || []).map((asset) => ({
          ...asset,
          type: "crypto",
          category: "Current Assets",
        })),
        ...(fixedData.fixed_assets || []).map((asset) => ({
          ...asset,
          type: "fixed",
          category: "Fixed Assets",
        })),
        ...(tradingData.trading_assets || []).map((asset) => ({
          ...asset,
          type: "trading",
          category: "Trading Assets",
        })),
      ];

      setAssets(allAssets);
    } catch (error) {
      console.error("Error fetching assets:", error);
      alert("Failed to fetch assets");
    }
    setIsLoading(false);
  };

  const filterAssets = () => {
    let filtered = assets;

    if (selectedCategory === "current") {
      filtered = filtered.filter(
        (asset) =>
          asset.category === "Current Assets" && asset.type === selectedSubType
      );
    } else if (selectedCategory === "fixed") {
      filtered = filtered.filter((asset) => asset.category === "Fixed Assets");
    } else if (selectedCategory === "trading") {
      filtered = filtered.filter(
        (asset) => asset.category === "Trading Assets"
      );
    }

    setFilteredAssets(filtered);
  };

  const handleView = (asset) => {
    setSelectedAsset(asset);
    setModalType("view");
    setShowModal(true);
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setModalType("edit");
    setShowModal(true);
  };

  const handleDelete = (asset) => {
    setSelectedAsset(asset);
    setModalType("delete");
    setShowModal(true);
  };

  const handleAssetUpdate = () => {
    fetchAssets();
  };

  const handleAssetDelete = () => {
    fetchAssets();
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case "cash":
        return <FaCoins className="w-5 h-5 text-green-500" />;
      case "bank":
        return <FaUniversity className="w-5 h-5 text-blue-500" />;
      case "crypto":
        return <FaBitcoin className="w-5 h-5 text-orange-500" />;
      case "fixed":
        return <FaCog className="w-5 h-5 text-purple-500" />;
      case "trading":
        return <FaBox className="w-5 h-5 text-emerald-500" />;
      default:
        return <FaBox className="w-5 h-5 text-slate-500" />;
    }
  };

  const getAssetTypeName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Account";
      case "bank":
        return "Bank Account";
      case "crypto":
        return "Crypto Account";
      case "fixed":
        return "Fixed Asset";
      case "trading":
        return "Trading Asset";
      default:
        return "Asset";
    }
  };

  const getAssetDisplayName = (asset) => {
    switch (asset.type) {
      case "cash":
        return asset.name;
      case "bank":
        return asset.bank_name;
      case "crypto":
        return `${asset.exchange_name} - ${asset.token_name}`;
      case "fixed":
      case "trading":
        return asset.name;
      default:
        return "Unknown Asset";
    }
  };

  const getAssetValue = (asset) => {
    switch (asset.type) {
      case "cash":
      case "bank":
        return `${parseFloat(asset.amount || 0).toFixed(2)}`;
      case "crypto":
        return `${parseFloat(asset.no_of_tokens || 0).toFixed(8)} ${
          asset.token_name
        }`;
      case "fixed":
      case "trading":
        return "N/A";
      default:
        return "N/A";
    }
  };



  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          Asset Management
        </h2>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl mx-auto">
          View, manage, and track all your registered assets
        </p>
      </div>

      {/* Asset Type Tabs */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg p-6">
        <h3 className="text-2xl font-bold text-slate-800 mb-6">
          Filter by Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            className={`p-4 border-2 rounded-2xl transition-all duration-300 text-center ${
              selectedCategory === "current"
                ? "border-green-500 bg-gradient-to-br from-green-50 to-green-100/80 shadow-xl"
                : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
            }`}
            onClick={() => setSelectedCategory("current")}
          >
            <FaCoins className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <span className="text-sm font-semibold">Current Assets</span>
          </button>
          <button
            className={`p-4 border-2 rounded-2xl transition-all duration-300 text-center ${
              selectedCategory === "fixed"
                ? "border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100/80 shadow-xl"
                : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
            }`}
            onClick={() => setSelectedCategory("fixed")}
          >
            <FaCog className="w-6 h-6 mx-auto mb-2 text-purple-500" />
            <span className="text-sm font-semibold">Fixed Assets</span>
          </button>
          <button
            className={`p-4 border-2 rounded-2xl transition-all duration-300 text-center ${
              selectedCategory === "trading"
                ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100/80 shadow-xl"
                : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
            }`}
            onClick={() => setSelectedCategory("trading")}
          >
            <FaBox className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            <span className="text-sm font-semibold">Trading Assets</span>
          </button>
        </div>

        {/* Sub-tabs for Current Assets */}
        {selectedCategory === "current" && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-slate-800 mb-4">
              Current Asset Types
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                className={`p-4 border-2 rounded-2xl transition-all duration-300 text-center ${
                  selectedSubType === "cash"
                    ? "border-green-500 bg-gradient-to-br from-green-50 to-green-100/80 shadow-xl"
                    : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
                }`}
                onClick={() => setSelectedSubType("cash")}
              >
                <FaCoins className="w-6 h-6 mx-auto mb-2 text-green-500" />
                <span className="text-sm font-semibold">Cash Accounts</span>
              </button>
              <button
                className={`p-4 border-2 rounded-2xl transition-all duration-300 text-center ${
                  selectedSubType === "bank"
                    ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100/80 shadow-xl"
                    : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
                }`}
                onClick={() => setSelectedSubType("bank")}
              >
                <FaUniversity className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                <span className="text-sm font-semibold">Bank Accounts</span>
              </button>
              <button
                className={`p-4 border-2 rounded-2xl transition-all duration-300 text-center ${
                  selectedSubType === "crypto"
                    ? "border-orange-500 bg-gradient-to-br from-orange-50 to-orange-100/80 shadow-xl"
                    : "border-slate-200 bg-white/80 hover:border-slate-300 hover:shadow-lg"
                }`}
                onClick={() => setSelectedSubType("crypto")}
              >
                <FaBitcoin className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                <span className="text-sm font-semibold">Crypto Accounts</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Assets List */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-2xl font-bold text-slate-800 flex items-center">
            <FaBox className="w-6 h-6 mr-3 text-blue-500" />
            Asset Inventory
          </h3>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-slate-600">Loading assets...</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="p-12 text-center">
            <FaBox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h4 className="text-xl font-semibold text-slate-600 mb-2">
              No Assets Found
            </h4>
            <p className="text-slate-500">
              No assets found in this category. Try selecting a different
              category or register a new asset.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Type
                  </th>
                  {selectedCategory === "current" && (
                    <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                      Value
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredAssets.map((asset, index) => (
                  <tr
                    key={`${asset.type}-${asset.id}`}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getAssetIcon(asset.type)}
                        <div className="ml-3">
                          <p className="text-sm font-medium text-slate-900">
                            {getAssetDisplayName(asset)}
                          </p>
                          {asset.description && (
                            <p className="text-sm text-slate-500 truncate max-w-xs">
                              {asset.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getAssetTypeName(asset.type)}
                      </span>
                    </td>
                    {selectedCategory === "current" && (
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {getAssetValue(asset)}
                      </td>
                    )}
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {new Date(asset.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(asset)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Asset"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(asset)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit Asset"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(asset)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Asset"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Asset Modal */}
      <AssetModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        asset={selectedAsset}
        modalType={modalType}
        onUpdate={handleAssetUpdate}
        onDelete={handleAssetDelete}
      />
    </div>
  );
};

export default AssetList;
